import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Typography, Card, Button } from '@equinor/eds-core-react'
import { useParams } from 'react-router-dom'
import { useQueries, UseQueryResult, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import { getTimeSeriesValues, getLatestTimeSeriesValues } from '@/api/apiService'
import { MAX_TREND_POINTS } from '@/api/realtimeUtils'
import { QueryKeys } from '@/core/models/enum'
import { ResponseObject } from '@/core/models/types'
import { TrendDto, TimeseriesLatestDto } from '@/api_generated/api'

type TrendPoint = { x: number; y: number }
type TrendSeries = { key: string; label: string; color: string; points: TrendPoint[] }
type TrendValuePoint = { time?: string; value?: number | null }

const DEFAULT_ALARM_LEVEL = 70
const Y_AXIS_LABEL = 'dB'
const DEFAULT_SCREEN_TITLE = 'Noise level in the Rotvoll cantina'
const DISPLAY_TIME_ZONE = 'Europe/Oslo'
const POLL_LATEST_VALUES = ['true', 'false']

const Page = styled.div`
    padding: 24px;
    max-width: 1100px;
    margin: 0 auto;
`

const HeaderRow = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 10px;
`

const SubRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin: 10px 0 8px;
`

const Controls = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
`

const Field = styled.label`
    display: inline-flex;
    flex-direction: column;
    gap: 4px;
`

const ControlInput = styled.input`
    padding: 6px 10px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    background: white;
`

const ControlSelect = styled.select`
    padding: 6px 10px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    background: white;
`

const Legend = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`

const LegendItem = styled(Button)<{ $active: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    opacity: ${(p) => (p.$active ? 1 : 0.55)};
`

const Swatch = styled.span<{ $color: string }>`
    width: 10px;
    height: 10px;
    border-radius: 999px;
    background: ${(p) => p.$color};
    display: inline-block;
`

const ChartWrap = styled.div<{ $height: number }>`
    width: 100%;
    height: ${(p) => p.$height}px;
`

const Svg = styled.svg`
    width: 100%;
    height: 100%;
    display: block;
`

const MetaRow = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 10px;
    flex-wrap: wrap;
    opacity: 0.95;
`

const Pill = styled.div`
    padding: 6px 10px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.06);
`

const ErrorBox = styled.div`
    padding: 12px;
    border-radius: 8px;
    border: 1px solid rgba(220, 0, 0, 0.35);
`

const SERIES_COLORS = ['#007079', '#FF9200', '#243746', '#A8CED1', '#6F7D8C', '#7D2D8C', '#3D8B37']

function niceStep(rawStep: number) {
    const pow = Math.pow(10, Math.floor(Math.log10(rawStep)))
    const n = rawStep / pow
    const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10
    return nice * pow
}

function niceTimeStep(spanMs: number, targetTicks: number) {
    const raw = spanMs / Math.max(2, targetTicks - 1)
    const steps = [
        1_000,
        2_000,
        5_000,
        10_000,
        15_000,
        30_000,
        60_000,
        2 * 60_000,
        5 * 60_000,
        10 * 60_000,
        15 * 60_000,
        30 * 60_000,
        60 * 60_000,
        2 * 60 * 60_000,
        6 * 60 * 60_000,
        12 * 60 * 60_000,
        24 * 60 * 60_000,
        2 * 24 * 60 * 60_000,
        7 * 24 * 60 * 60_000,
    ]
    return steps.find((s) => s >= raw) ?? steps[steps.length - 1]
}

function ceilToStep(v: number, step: number) {
    return Math.ceil(v / step) * step
}

function linScale(domain: [number, number], range: [number, number]) {
    const [d0, d1] = domain
    const [r0, r1] = range
    const m = d1 === d0 ? 0 : (r1 - r0) / (d1 - d0)
    return (v: number) => r0 + (v - d0) * m
}

function buildPath(points: Array<{ x: number; y: number }>) {
    if (points.length === 0) return ''
    const [first, ...rest] = points
    return `M ${first.x} ${first.y} ` + rest.map((p) => `L ${p.x} ${p.y}`).join(' ')
}

function formatDateTime(ms: number) {
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: DISPLAY_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).format(new Date(ms))
}

function formatTimeTick(ms: number, spanMs: number) {
    const d = new Date(ms)

    if (spanMs <= 6 * 60 * 60 * 1000) {
        return new Intl.DateTimeFormat('en-GB', {
            timeZone: DISPLAY_TIME_ZONE,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        }).format(d)
    }

    if (spanMs <= 48 * 60 * 60 * 1000) {
        return new Intl.DateTimeFormat('en-GB', {
            timeZone: DISPLAY_TIME_ZONE,
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        }).format(d)
    }

    return new Intl.DateTimeFormat('en-GB', {
        timeZone: DISPLAY_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(d)
}

function formatY(y: number) {
    return Number.isFinite(y) ? `${Math.round(y)}` : ''
}

function parseNumberParam(value: string | undefined, defaultValue: number) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : defaultValue
}

function parseBooleanParam(value: string | undefined, defaultValue: boolean) {
    if (value === 'true') return true
    if (value === 'false') return false
    return defaultValue
}

function parseDateParam(value: string | undefined) {
    if (!value?.trim()) return undefined

    const parsed = new Date(value).getTime()
    return Number.isFinite(parsed) ? parsed : undefined
}

function formatDateTimeLocalInput(ms: number | undefined) {
    if (ms === undefined) return ''

    const date = new Date(ms)
    const localDate = new Date(ms - date.getTimezoneOffset() * 60_000)
    return localDate.toISOString().slice(0, 16)
}

function takeLastN<T>(arr: T[], n: number) {
    if (n <= 0) return []
    if (arr.length <= n) return arr
    return arr.slice(arr.length - n)
}

function getTrendValues(trend: TrendDto): TrendValuePoint[] {
    const values = (trend as unknown as { values?: TrendValuePoint[] }).values
    return Array.isArray(values) ? values : []
}

function extractRawPointsFromTrendDtoList(trends: TrendDto[]): Array<{ t: number; v: number }> {
    const raw: Array<{ t: number; v: number }> = []

    for (const trend of trends ?? []) {
        const values = getTrendValues(trend)
        if (!values.length) continue

        for (const dp of values) {
            const ms = dp.time ? new Date(dp.time).getTime() : NaN
            const value = dp.value

            if (!Number.isNaN(ms) && typeof value === 'number' && Number.isFinite(value)) {
                raw.push({ t: ms, v: value })
            }
        }
    }

    raw.sort((a, b) => a.t - b.t)
    return raw
}

function buildSeriesFromManyIds(
    results: Array<{ id: string; data: TrendDto[] }>,
    maxPointsPerSeries: number,
    orderedIds: string[],
    fromMs?: number
): TrendSeries[] {
    return results
        .map(({ id, data }) => {
            const raw = extractRawPointsFromTrendDtoList(data)
            const filtered = fromMs === undefined ? raw : raw.filter((p) => p.t >= fromMs)
            if (!filtered.length) return null

            const windowed = takeLastN(filtered, maxPointsPerSeries)
            const sensorIndex = orderedIds.indexOf(id)

            return {
                key: id,
                label: `Sensor ${sensorIndex + 1}`,
                color: SERIES_COLORS[sensorIndex % SERIES_COLORS.length],
                points: windowed.map((p) => ({ x: p.t, y: p.v })),
            }
        })
        .filter(Boolean) as TrendSeries[]
}

function pickErrorMessage(err: unknown): string {
    const e = err as AxiosError<ResponseObject> | Error | undefined
    if (!e) return 'Unknown error'
    if ('isAxiosError' in (e as any)) {
        const ax = e as AxiosError<ResponseObject>
        return ax.response?.data?.message ?? ax.message
    }
    return e.message
}

function extractLatestPoint(latest: TimeseriesLatestDto): { time: string; value: number } | null {
    const latestAny = latest as any

    if (latestAny?.time && typeof latestAny?.value === 'number' && Number.isFinite(latestAny.value)) {
        return { time: latestAny.time, value: latestAny.value }
    }

    if (
        latestAny?.value?.time &&
        typeof latestAny?.value?.value === 'number' &&
        Number.isFinite(latestAny.value.value)
    ) {
        return { time: latestAny.value.time, value: latestAny.value.value }
    }

    if (Array.isArray(latestAny?.values) && latestAny.values.length > 0) {
        const last = latestAny.values[latestAny.values.length - 1]
        if (last?.time && typeof last?.value === 'number' && Number.isFinite(last.value)) {
            return { time: last.time, value: last.value }
        }
    }

    return null
}

function appendLatestIntoTrendDtoArray(
    oldData: TrendDto[] | undefined,
    latest: TimeseriesLatestDto
): TrendDto[] | undefined {
    const point = extractLatestPoint(latest)
    if (!point) return oldData

    if (!oldData || oldData.length === 0) {
        return [{ values: [point] } as unknown as TrendDto]
    }

    return oldData.map((trend, i) => {
        if (i !== 0) return trend

        const current = trend as unknown as { values?: TrendValuePoint[] }
        const values = Array.isArray(current.values) ? current.values : []
        const last = values[values.length - 1]

        const nextValues = last?.time === point.time ? [...values.slice(0, -1), point] : [...values, point]

        return {
            ...trend,
            values: nextValues.length > MAX_TREND_POINTS ? nextValues.slice(-MAX_TREND_POINTS) : nextValues,
        } as TrendDto
    })
}

export default function TrendPage() {
    const {
        timeSeriesTableIds = '',
        pollLatest,
        yAxisLabel: yAxisLabelParam,
        alarmLevel: alarmLevelParam,
        showAlarm: showAlarmParam,
        screenTitle: screenTitleParam,
        fromDate: fromDateParam,
    } = useParams<{
        timeSeriesTableIds: string
        pollLatest?: string
        yAxisLabel?: string
        alarmLevel?: string
        showAlarm?: string
        screenTitle?: string
        fromDate?: string
    }>()
    const shouldPollLatest = pollLatest === 'true'
    const initialYAxisLabel =
        yAxisLabelParam?.trim() ||
        (!POLL_LATEST_VALUES.includes(pollLatest ?? '') && pollLatest?.trim()) ||
        Y_AXIS_LABEL
    const initialAlarmLevel = parseNumberParam(alarmLevelParam, DEFAULT_ALARM_LEVEL)
    const initialShowAlarm = parseBooleanParam(showAlarmParam, true)
    const initialScreenTitle = screenTitleParam?.trim() || DEFAULT_SCREEN_TITLE
    const initialFromDate = formatDateTimeLocalInput(parseDateParam(fromDateParam) ?? Date.now() - 24 * 60 * 60 * 1000)

    const [fromDateInput, setFromDateInput] = useState(initialFromDate)

    const yAxisLabel = initialYAxisLabel
    const alarmLevel = initialAlarmLevel
    const showAlarm = initialShowAlarm
    const screenTitle = initialScreenTitle
    const fromMs = parseDateParam(fromDateInput)
    const fromQuery = fromMs === undefined ? undefined : new Date(fromMs).toISOString()

    const ids = useMemo(
        () =>
            timeSeriesTableIds
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
        [timeSeriesTableIds]
    )

    const queryClient = useQueryClient()
    const [windowSize, setWindowSize] = useState(500)
    const [visible, setVisible] = useState<Record<string, boolean>>({})

    const queries = useQueries({
        queries: ids.map((id) => ({
            queryKey: [QueryKeys.Trends, id, fromQuery],
            queryFn: () => getTimeSeriesValues(id, fromQuery),
            enabled: Boolean(id),
            refetchOnMount: false,
            refetchOnWindowFocus: false,
        })),
    }) as Array<UseQueryResult<TrendDto[], AxiosError<ResponseObject>>>

    useEffect(() => {
        if (!shouldPollLatest || ids.length === 0) return

        let isCancelled = false

        const tick = async () => {
            try {
                const latestList = await Promise.all(ids.map((id) => getLatestTimeSeriesValues(id)))
                if (isCancelled) return

                for (let i = 0; i < ids.length; i++) {
                    const id = ids[i]
                    const latest = latestList[i]

                    queryClient.setQueryData<TrendDto[]>([QueryKeys.Trends, id, fromQuery], (old) =>
                        appendLatestIntoTrendDtoArray(old, latest)
                    )
                }
            } catch {
                //
            }
        }

        tick()
        const handle = window.setInterval(tick, 5 * 60 * 1000)

        return () => {
            isCancelled = true
            window.clearInterval(handle)
        }
    }, [shouldPollLatest, ids, queryClient, fromQuery])

    const isLoading = queries.some((q) => q.isLoading)
    const firstError = queries.find((q) => q.isError)?.error
    const errorMessage = firstError ? pickErrorMessage(firstError) : undefined

    const results = useMemo(
        () =>
            ids.map((id, i) => ({
                id,
                data: queries[i]?.data ?? [],
            })),
        [ids, queries]
    )

    const fullRange = useMemo(() => {
        const allRawPoints = results.flatMap((r) => extractRawPointsFromTrendDtoList(r.data))
        if (allRawPoints.length === 0) return null

        const xs = allRawPoints.map((p) => p.t)

        return {
            from: Math.min(...xs),
            to: Math.max(...xs),
            totalPoints: allRawPoints.length,
        }
    }, [results])

    const series = useMemo(() => buildSeriesFromManyIds(results, windowSize, ids), [results, windowSize, ids])

    useEffect(() => {
        if (!series.length) return

        setVisible((prev) => {
            let changed = false
            const next = { ...prev }

            for (const s of series) {
                if (next[s.key] === undefined) {
                    next[s.key] = true
                    changed = true
                }
            }

            return changed ? next : prev
        })
    }, [series])

    const visibleSeries = useMemo(() => series.filter((s) => visible[s.key] !== false), [series, visible])

    const computed = useMemo(() => {
        if (visibleSeries.length === 0) return null

        const allPts = visibleSeries.flatMap((s) => s.points)
        if (allPts.length === 0) return null

        const xs = allPts.map((p) => p.x)
        const ys = allPts.map((p) => p.y)

        const xMin = Math.min(...xs)
        const xMax = Math.max(...xs)

        const yMinRaw = Math.min(...ys)
        const yMaxRaw = Math.max(...ys)

        const yMinWithAlarm = showAlarm ? Math.min(yMinRaw, alarmLevel) : yMinRaw
        const yMaxWithAlarm = showAlarm ? Math.max(yMaxRaw, alarmLevel) : yMaxRaw

        const yPad = yMaxWithAlarm === yMinWithAlarm ? 1 : (yMaxWithAlarm - yMinWithAlarm) * 0.1

        return {
            xMin,
            xMax,
            yMin: yMinWithAlarm - yPad,
            yMax: yMaxWithAlarm + yPad,
            totalPoints: allPts.length,
        }
    }, [visibleSeries, alarmLevel, showAlarm])

    const W = 900
    const H = 360
    const height = 320

    const margin = { top: 18, right: 14, bottom: 58, left: 72 }
    const innerW = W - margin.left - margin.right
    const innerH = H - margin.top - margin.bottom

    const axisColor = 'rgba(0,0,0,0.55)'
    const gridColor = 'rgba(0,0,0,0.10)'

    const chart = useMemo(() => {
        if (!computed) return null

        const xScale = linScale([computed.xMin, computed.xMax], [0, innerW])
        const yScale = linScale([computed.yMin, computed.yMax], [innerH, 0])

        const ticks = 6
        const xSpan = computed.xMax - computed.xMin || 1
        const ySpan = computed.yMax - computed.yMin || 1

        const xStep = niceTimeStep(xSpan, ticks)
        const yStep = niceStep(ySpan / Math.max(2, ticks - 1))

        const xTickStart = ceilToStep(computed.xMin, xStep)
        const yTickStart = Math.ceil(computed.yMin / yStep) * yStep

        const xTicks: number[] = []
        for (let v = xTickStart; v <= computed.xMax + 1e-9; v += xStep) xTicks.push(v)

        const yTicks: number[] = []
        for (let v = yTickStart; v <= computed.yMax + 1e-9; v += yStep) yTicks.push(v)

        const seriesPaths = visibleSeries.map((s) => {
            const plotPoints = s.points.map((p) => ({
                x: margin.left + xScale(p.x),
                y: margin.top + yScale(p.y),
            }))
            return { key: s.key, label: s.label, color: s.color, d: buildPath(plotPoints) }
        })

        return { xTicks, yTicks, xScale, yScale, seriesPaths }
    }, [computed, visibleSeries, innerW, innerH])

    const toggleSeries = (key: string) => {
        setVisible((prev) => ({ ...prev, [key]: prev[key] === false }))
    }

    const anyIds = ids.length > 0

    const seriesByKey = useMemo(() => {
        const m = new Map<string, TrendSeries>()
        for (const s of visibleSeries) m.set(s.key, s)
        return m
    }, [visibleSeries])

    return (
        <Page>
            <HeaderRow>
                <div style={{ flex: 1 }}>
                    <Typography variant="h4" style={{ textAlign: 'center' }}>
                        {screenTitle}
                    </Typography>
                </div>

                <div style={{ minWidth: 80, textAlign: 'right' }}>
                    <Typography variant="caption">{isLoading ? 'Loading…' : ''}</Typography>
                </div>
            </HeaderRow>

            <Card style={{ padding: 16 }}>
                {!anyIds ? (
                    <Typography variant="body_short">
                        No ids provided. Use <code>/trends/id1</code> or <code>/trends/id1,id2,id3</code>
                        <br />
                        Enable latest polling: <code>/trends/id1/true</code>
                        <br />
                        Set y-axis label: <code>/trends/id1/false/dB</code>
                        <br />
                        Set alarm: <code>/trends/id1/false/dB/70/true</code>
                        <br />
                        Set title: <code>/trends/id1/false/dB/70/true/Noise%20level</code>
                        <br />
                        Set from date: <code>/trends/id1/false/dB/70/true/Noise%20level/2026-06-18T08%3A00%3A00Z</code>
                    </Typography>
                ) : errorMessage ? (
                    <ErrorBox>
                        <Typography variant="body_short" color="danger">
                            {errorMessage}
                        </Typography>
                    </ErrorBox>
                ) : isLoading ? (
                    <Typography variant="body_short">Loading trend data…</Typography>
                ) : (
                    <>
                        <SubRow>
                            <Controls>
                                <Field>
                                    <Typography variant="caption">From date</Typography>
                                    <ControlInput
                                        type="datetime-local"
                                        value={fromDateInput}
                                        onChange={(e) => setFromDateInput(e.target.value)}
                                        aria-label="Set from date"
                                    />
                                </Field>

                                <Field>
                                    <Typography variant="caption">Window</Typography>
                                    <ControlSelect
                                        value={windowSize}
                                        onChange={(e) => setWindowSize(Number(e.target.value))}
                                        aria-label="Select window size"
                                    >
                                        <option value={1}>Latest 1</option>
                                        <option value={10}>Latest 10</option>
                                        <option value={100}>Latest 100</option>
                                        <option value={500}>Latest 500</option>
                                        <option value={2000}>Latest 2000</option>
                                        <option value={10000}>Latest 10000</option>
                                    </ControlSelect>
                                </Field>
                            </Controls>

                            {series.length > 0 && (
                                <Legend aria-label="Trend legend">
                                    {series.map((s) => {
                                        const active = visible[s.key] !== false
                                        return (
                                            <LegendItem
                                                key={s.key}
                                                variant="ghost"
                                                $active={active}
                                                aria-pressed={active}
                                                onClick={() => toggleSeries(s.key)}
                                                title={active ? 'Hide series' : 'Show series'}
                                            >
                                                <Swatch $color={s.color} />
                                                <Typography variant="caption">{s.label}</Typography>
                                            </LegendItem>
                                        )
                                    })}
                                </Legend>
                            )}
                        </SubRow>

                        {series.length === 0 ? (
                            <Typography variant="body_short">
                                {fullRange?.totalPoints
                                    ? 'No data in selected date range.'
                                    : 'No data returned for any id.'}
                            </Typography>
                        ) : visibleSeries.length === 0 ? (
                            <Typography variant="body_short">All series are hidden.</Typography>
                        ) : (
                            <>
                                <ChartWrap $height={height}>
                                    <Svg
                                        viewBox={`0 0 ${W} ${H}`}
                                        preserveAspectRatio="none"
                                        role="img"
                                        aria-label="Trend chart"
                                    >
                                        {chart?.yTicks.map((v, i) => {
                                            const yy = margin.top + chart.yScale(v)
                                            return (
                                                <g key={`y-${i}`}>
                                                    <line
                                                        x1={margin.left}
                                                        y1={yy}
                                                        x2={W - margin.right}
                                                        y2={yy}
                                                        stroke={gridColor}
                                                        strokeWidth="1"
                                                    />
                                                    <text
                                                        x={margin.left - 10}
                                                        y={yy}
                                                        textAnchor="end"
                                                        dominantBaseline="middle"
                                                        fontSize="12"
                                                        fill={axisColor}
                                                    >
                                                        {formatY(v)}
                                                    </text>
                                                </g>
                                            )
                                        })}

                                        {chart?.xTicks.map((v, i) => {
                                            const xx = margin.left + chart.xScale(v)
                                            const labelY = H - margin.bottom + 24
                                            const span = computed ? computed.xMax - computed.xMin : 0

                                            return (
                                                <g key={`x-${i}`}>
                                                    <line
                                                        x1={xx}
                                                        y1={margin.top}
                                                        x2={xx}
                                                        y2={H - margin.bottom}
                                                        stroke={gridColor}
                                                        strokeWidth="1"
                                                    />
                                                    <text
                                                        x={xx}
                                                        y={labelY}
                                                        textAnchor="middle"
                                                        dominantBaseline="middle"
                                                        fontSize="12"
                                                        fill={axisColor}
                                                    >
                                                        {formatTimeTick(v, span)}
                                                    </text>
                                                </g>
                                            )
                                        })}

                                        <line
                                            x1={margin.left}
                                            y1={H - margin.bottom}
                                            x2={W - margin.right}
                                            y2={H - margin.bottom}
                                            stroke={axisColor}
                                            strokeWidth="1.2"
                                        />
                                        <line
                                            x1={margin.left}
                                            y1={margin.top}
                                            x2={margin.left}
                                            y2={H - margin.bottom}
                                            stroke={axisColor}
                                            strokeWidth="1.2"
                                        />

                                        <text
                                            x={22}
                                            y={margin.top + innerH / 2}
                                            transform={`rotate(-90 22 ${margin.top + innerH / 2})`}
                                            textAnchor="middle"
                                            fontSize="13"
                                            fill={axisColor}
                                            fontWeight="600"
                                        >
                                            {yAxisLabel}
                                        </text>

                                        {showAlarm && chart && (
                                            <g>
                                                {(() => {
                                                    const yAlarm = margin.top + chart.yScale(alarmLevel)
                                                    return (
                                                        <>
                                                            <line
                                                                x1={margin.left}
                                                                y1={yAlarm}
                                                                x2={W - margin.right}
                                                                y2={yAlarm}
                                                                stroke="rgba(235, 0, 55, 0.9)"
                                                                strokeWidth="2"
                                                                strokeDasharray="6 6"
                                                                vectorEffect="non-scaling-stroke"
                                                            />
                                                            <text
                                                                x={W - margin.right}
                                                                y={yAlarm - 6}
                                                                textAnchor="end"
                                                                fontSize="12"
                                                                fill="rgba(235, 0, 55, 0.9)"
                                                                fontWeight="600"
                                                            >
                                                                Alarm {formatY(alarmLevel)}
                                                            </text>
                                                        </>
                                                    )
                                                })()}
                                            </g>
                                        )}

                                        {chart?.seriesPaths.map((s) => {
                                            const ser = seriesByKey.get(s.key)
                                            if (!ser || ser.points.length === 0) return null

                                            const points = ser.points

                                            const line =
                                                points.length >= 2 ? (
                                                    <path
                                                        d={s.d}
                                                        fill="none"
                                                        stroke={s.color}
                                                        strokeWidth="2.2"
                                                        vectorEffect="non-scaling-stroke"
                                                    />
                                                ) : null

                                            const dots = points.map((p, idx) => {
                                                const cx = margin.left + chart.xScale(p.x)
                                                const cy = margin.top + chart.yScale(p.y)

                                                return (
                                                    <circle
                                                        key={`${s.key}-pt-${idx}`}
                                                        cx={cx}
                                                        cy={cy}
                                                        r={3.5}
                                                        fill={s.color}
                                                    />
                                                )
                                            })

                                            return (
                                                <g key={s.key}>
                                                    {line}
                                                    {dots}
                                                </g>
                                            )
                                        })}
                                    </Svg>
                                </ChartWrap>

                                {computed && (
                                    <MetaRow>
                                        <Pill>
                                            <Typography variant="caption">Series: {visibleSeries.length}</Typography>
                                        </Pill>

                                        <Pill>
                                            <Typography variant="caption">
                                                Visible points: {computed.totalPoints}
                                            </Typography>
                                        </Pill>

                                        <Pill>
                                            <Typography variant="caption">
                                                Total points: {fullRange?.totalPoints ?? 0}
                                            </Typography>
                                        </Pill>

                                        <Pill>
                                            <Typography variant="caption">
                                                Data from: {fullRange ? formatDateTime(fullRange.from) : '-'}
                                            </Typography>
                                        </Pill>

                                        <Pill>
                                            <Typography variant="caption">
                                                Data to: {fullRange ? formatDateTime(fullRange.to) : '-'}
                                            </Typography>
                                        </Pill>

                                        <Pill>
                                            <Typography variant="caption">
                                                Y: {Math.round(computed.yMin)} – {Math.round(computed.yMax)}{' '}
                                                {yAxisLabel}
                                            </Typography>
                                        </Pill>
                                    </MetaRow>
                                )}
                            </>
                        )}
                    </>
                )}
            </Card>
        </Page>
    )
}
